import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { Slide, SlideProps, Snackbar, SnackbarContent } from '@mui/material';
import { amber } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import classNames from 'classnames';
import { FC, useContext } from 'react';
import {
  ContextNotification,
  NotificationContext,
} from './NotificationContext';

const variantIcon = {
  success: CheckCircleIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  info: InfoIcon,
};

export const useStyles = makeStyles((theme) => ({
  success: {
    backgroundColor: theme.palette.primary.main,
  },
  error: {
    backgroundColor: theme.palette.error.dark,
  },
  info: {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.dark,
  },
  warning: {
    backgroundColor: amber[700],
  },
  icon: {
    fontSize: 20,
  },
  iconVariant: {
    opacity: 0.9,
    marginRight: theme.spacing(1),
  },
  message: {
    width: 400,
    display: 'flex',
  },
}));

function TransitionRight(props: SlideProps) {
  return <Slide {...props} direction="right" />;
}

export const NotificationDisplay: FC = () => {
  const classes = useStyles();
  const context = useContext(NotificationContext);

  if (!context) return null;

  return (
    <div>
      {context.notificationStack.map(
        (notification: ContextNotification, index: number) => {
          const Icon = variantIcon[notification.type];
          let prevHeight = 32;
          if (context.notificationStack[index - 1]) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            prevHeight = context.notificationStack[index - 1].height! + 42;
          }

          return (
            <Snackbar
              ref={(node: HTMLDivElement) => {
                !!node && context.setHeight(node, notification.key);
              }}
              key={notification.key}
              data-testid="notification-wrapper"
              open={notification.open}
              TransitionComponent={TransitionRight}
              TransitionProps={{
                onExited: () => {
                  context.exitedNotification(notification.key);
                },
              }}
              autoHideDuration={
                notification.autoHideDuration ||
                notification.autoHideDuration === false
                  ? undefined
                  : 5000
              }
              onClick={() => {
                context.removeNotification(notification.key);
              }}
              onClose={(event, reason) => {
                if (reason === 'clickaway') {
                  return;
                }
                context.removeNotification(notification.key);
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              style={{ marginBottom: prevHeight }}
            >
              <SnackbarContent
                className={classNames(classes[notification.type])}
                message={
                  <span className={classes.message}>
                    <Icon
                      className={classNames(classes.icon, classes.iconVariant)}
                    />
                    {notification.message}
                  </span>
                }
                action={notification.action || undefined}
              />
            </Snackbar>
          );
        },
      )}
    </div>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'You need to wrap useNotification inside NotificationProvider',
    );
  }
  return context;
};
export const NotificationContextConsumer = NotificationContext.Consumer;
